// NPM Imports
import { cloneDeep } from "lodash";

// Store Imports
import store from "../../store";

// Services Imports
import {
  getAllTeammatesUsersService,
  getAllRolesService,
  inviteUsersService,
  referralUsersService,
  removeRoleFromUserService,
  addRoleToUserService,
  disableEnableUserService,
  toggleRoleService,
  getRoleDetailsService,
  getAllPermissionsService,
  updateRoleService,
  createRoleService,
  createUserGroupService,
  fetchUserGroupsService,
  deleteUserGroupByIdService,
  changeUserGroupStatusService,
  editInviteUsersService,
  getTeammatesByGroupService,
} from "./userService";

import {
  addCheckFlagToPermissions,
  categorizePermissions,
  updateTeammateList,
} from "./userMethods";

import {
  arrayHasObjectWithKeyAndVal,
  writeDataToLocalStorage,
} from "../../utils/globalUtils";
import { searchUser } from "../PipelineActions/pipelineServices";

// Internal Import
import { AmplitudeUtils } from "../../utils";
import { EVENTS } from "../../constants";

export const initTeammatesSettingsData = () => async dispatch => {
  try {
    const users = await getAllTeammatesUsersService();
    const roles = await getAllRolesService();
    const groups = await fetchUserGroupsService();

    Promise.all([users, roles, groups])
      .then(response => {
        dispatch({
          type: "TEAMMATES_INIT_DATA",
          payload: response,
        });
      })
      .catch(error => {
        console.error(`userActions => initTeammatesSettingsData => ${error}`);
        throw error;
      });
    return null;
  } catch (error) {
    throw error;
  }
};

export const getAllTeammatesUsers = () => dispatch =>
  new Promise((resolve, reject) => {
    getAllTeammatesUsersService()
      .then(response => {
        dispatch({
          type: "TEAMMATES_LIST",
          payload: response.users,
        });
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getTeammatesByGroup = groupId => dispatch =>
  new Promise((resolve, reject) => {
    getTeammatesByGroupService(groupId)
      .then(response => {
        dispatch({
          type: "TEAMMATES_LIST",
          payload: response.users,
        });
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const createUserGroup = data => dispatch =>
  new Promise((resolve, reject) => {
    createUserGroupService(data)
      .then(response => {
        dispatch({
          type: "CREATE_USER_GROUP",
          payload: response,
        });
        resolve(response);
      })
      .catch(error => {
        console.error(`settingsActions => createUserGroup => ${error}`);
        reject(error);
      });
  });

export const deleteUserGroupById = groupId => dispatch =>
  new Promise((resolve, reject) => {
    deleteUserGroupByIdService(groupId)
      .then(response => {
        const reduxState = store.getState();
        const { userGroups, teammatesList } = reduxState.SettingsStore;
        let updatedGroups = cloneDeep(userGroups);
        const updatedteammatesList = cloneDeep(teammatesList);
        updatedGroups = updatedGroups.filter(group => group.id !== groupId);
        // delete the current group from existing teammates list
        updatedteammatesList.forEach(teammate => {
          const updatedUGroups = teammate.usergroups.filter(
            userGroup => userGroup.id !== groupId,
          );
          teammate.usergroups = updatedUGroups;
        });
        const payload = {
          teammatesList: updatedteammatesList,
          userGroups: updatedGroups,
        };
        dispatch({
          type: "DELETE_USER_GROUP",
          payload,
        });
        resolve(response);
      })
      .catch(error => {
        console.error(`settingsActions => deleteUserGroupById => ${error}`);
        reject(error);
      });
  });

export const changeUserGroupStatus = data => dispatch =>
  new Promise((resolve, reject) => {
    changeUserGroupStatusService(data)
      .then(response => {
        const reduxState = store.getState();
        const { userGroups, teammatesList } = reduxState.SettingsStore;
        const updatedGroups = cloneDeep(userGroups);
        const updatedteammatesList = cloneDeep(teammatesList);
        updatedGroups.forEach(userGroup => {
          if (userGroup.id === data.id) {
            userGroup.active = data.active;
          }
        });
        updatedteammatesList.forEach(teammate => {
          teammate.usergroups.forEach(userGroup => {
            if (userGroup.id === data.id) {
              userGroup.active = data.active;
            }
          });
        });
        const payload = {
          teammatesList: updatedteammatesList,
          userGroups: updatedGroups,
        };
        dispatch({
          type: "CHANGE_USER_GROUP_STATUS",
          payload,
        });
        resolve(response);
      })
      .catch(error => {
        console.error(`settingsActions => changeUserGroupStatus => ${error}`);
        reject(error);
      });
  });

export const getAllRoles = () => dispatch =>
  new Promise((resolve, reject) => {
    getAllRolesService()
      .then(response => {
        dispatch({
          type: "ORG_ROLES_LIST",
          payload: response.Roles,
        });
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getAllPermissions = () => dispatch =>
  new Promise((resolve, reject) => {
    getAllPermissionsService()
      .then(response => {
        dispatch({
          type: "ORG_PERMISSIONS_LIST",
          payload: response.Permissions,
        });
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const getCategorizedPermissions = () => dispatch =>
  new Promise((resolve, reject) => {
    getAllPermissionsService()
      .then(response => {
        const reduxState = store.getState();
        const { roleDetails } = reduxState.SettingsStore;
        const permissionsWithChecks = addCheckFlagToPermissions(
          response.Permissions,
          roleDetails.permissions,
        );
        const categorizedPermissions = categorizePermissions(
          permissionsWithChecks,
        );
        dispatch({
          type: "ORG_PERMISSIONS_LIST",
          payload: response.Permissions,
        });
        dispatch({
          type: "ORG_PERMISSIONS_LIST_CATEGORIZED",
          payload: categorizedPermissions,
        });
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const updateCategorizedPermissionsView = permission => dispatch => {
  const reduxState = store.getState();
  const { roleDetails, orgPermissionsList } = reduxState.SettingsStore;
  let updatedRoleDetails;
  if (
    arrayHasObjectWithKeyAndVal(roleDetails.permissions, "id", permission.id)
  ) {
    // remove the selected permission
    updatedRoleDetails = {
      ...roleDetails,
      permissions: roleDetails.permissions.filter(
        item => item.id !== permission.id,
      ),
    };
  } else {
    // add the selected permission
    updatedRoleDetails = {
      ...roleDetails,
      permissions: [...roleDetails.permissions, permission],
    };
  }
  const permissionsWithChecks = addCheckFlagToPermissions(
    orgPermissionsList,
    updatedRoleDetails.permissions,
  );
  const categorizedPermissions = categorizePermissions(permissionsWithChecks);
  dispatch({
    type: "ROLE_DETAILS",
    payload: updatedRoleDetails,
  });
  dispatch({
    type: "ORG_PERMISSIONS_LIST_CATEGORIZED",
    payload: categorizedPermissions,
  });
};

export const inviteUsers = (users, isInviteOrReInviteCase) => dispatch =>
  new Promise((resolve, reject) => {
    inviteUsersService(users)
      .then(response => {
        const { success_invites: addedInvite } = response;
        let addedTeammate;
        // Added Amplitude for Teammates invites
        const data = {};
        if (addedInvite && addedInvite.length > 0) {
          addedTeammate = { ...addedInvite[0] };
        }
        if (addedTeammate.roles) {
          data.roles = addedTeammate.roles[0].name;
        }
        if (addedTeammate.usergroups.length > 0) {
          data.userGroups = true;
          data.groupName = addedTeammate.usergroups[0].name;
        } else {
          data.userGroups = false;
        }

        AmplitudeUtils.amplitudeLogEvent(
          EVENTS.EVENT_NAMES.TEAMMATES_INVITED,
          data,
        );
        dispatch({
          type: "TEAMMATES_LIST",
          payload: updateTeammateList(
            addedTeammate,
            false,
            isInviteOrReInviteCase,
          ),
        });
        resolve(response);
      })
      .catch(error => {
        console.error(`userActions => inviteUsers => ${error}`);
        reject(error);
      });
  });

export const editInviteUsers = users => dispatch =>
  new Promise((resolve, reject) => {
    editInviteUsersService(users)
      .then(response => {
        const { updated_invite: updatedTeammate } = response;
        dispatch({
          type: "TEAMMATES_LIST",
          payload: updateTeammateList(updatedTeammate, true, false),
        });
        resolve(response);
      })
      .catch(error => {
        console.error(`userActions => editInviteUsers => ${error}`);
        reject(error);
      });
  });

export const referUsers = (users, isInviteOrReInviteCase) => dispatch =>
  new Promise((resolve, reject) => {
    referralUsersService(users)
      .then(response => {
        dispatch({
          type: "REFERRAL_LIST",
          payload: updateTeammateList(false, isInviteOrReInviteCase),
        });
        resolve(response);
      })
      .catch(error => {
        console.error(`userActions => referUsers => ${error}`);
        reject(error);
      });
  });

export const removeRoleFromUser = (userID, roleID) => () =>
  new Promise((resolve, reject) => {
    removeRoleFromUserService(userID, roleID)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        console.error(`userActions => removeRoleFromUser => ${error}`);
        reject(error);
      });
  });

export const addRoleToUser = (userID, roleID) => () =>
  new Promise((resolve, reject) => {
    addRoleToUserService(userID, roleID)
      .then(() => {
        resolve("success");
      })
      .catch(error => {
        reject(error);
      });
  });

export const disableEnableUser = data => dispatch =>
  new Promise((resolve, reject) => {
    disableEnableUserService(data.userData)
      .then(response => {
        const reduxState = store.getState();
        const { teammatesList } = reduxState.SettingsStore;
        const updatedteammatesList = cloneDeep(teammatesList);
        updatedteammatesList.forEach(teammate => {
          if (teammate.id === data.teammateId) {
            teammate.is_active = data.userData.is_active;
          }
        });
        dispatch({
          type: "TEAMMATES_LIST",
          payload: updatedteammatesList,
        });
        resolve(response);
      })
      .catch(error => {
        reject(error);
      });
  });

export const toggleRole = roleID => () =>
  new Promise((resolve, reject) => {
    toggleRoleService(roleID)
      .then(() => {
        resolve("success");
      })
      .catch(error => {
        reject(error);
      });
  });

export const getRoleDetails = roleID => dispatch =>
  new Promise((resolve, reject) => {
    getRoleDetailsService(roleID)
      .then(response => {
        dispatch({
          type: "ROLE_DETAILS",
          payload: response.Role,
        });
        resolve("success");
      })
      .catch(error => {
        reject(error);
      });
  });

export const getUserPermissions = roleID => dispatch =>
  new Promise((resolve, reject) => {
    getRoleDetailsService(roleID)
      .then(response => {
        dispatch({
          type: "CURRENT_USER_PERMISSIONS",
          payload: response.Role.permissions,
        });
        resolve("success");
      })
      .catch(error => {
        reject(error);
      });
  });

export const writeUserPermissionsToLocalStorage = roleID => () =>
  new Promise((resolve, reject) => {
    getRoleDetailsService(roleID)
      .then(response => {
        writeDataToLocalStorage(
          "revvsalesUserPermissions",
          response.Role.permissions,
        );
        resolve("success");
      })
      .catch(error => {
        reject(error);
      });
  });

export const handleNewRoleName = nameText => dispatch => {
  const reduxState = store.getState();
  const { roleDetails } = reduxState.SettingsStore;
  dispatch({
    type: "ROLE_DETAILS",
    payload: { ...roleDetails, name: nameText },
  });
};

export const handleNewRoleDescription = descText => dispatch => {
  const reduxState = store.getState();
  const { roleDetails } = reduxState.SettingsStore;
  dispatch({
    type: "ROLE_DETAILS",
    payload: { ...roleDetails, description: descText },
  });
};

export const initialiseNewRoleDetails = mode => dispatch => {
  let newRoleDetails;

  if (mode === "add") {
    newRoleDetails = {
      name: "",
      description: "",
      permissions: [],
    };
  } else if (mode === "clone") {
    const reduxState = store.getState();
    const { roleDetails } = reduxState.SettingsStore;
    delete roleDetails.is_default;
    newRoleDetails = {
      ...roleDetails,
      name: `${roleDetails.name} - Copy`,
      description: "",
    };
  }

  dispatch({
    type: "ROLE_DETAILS",
    payload: newRoleDetails,
  });
};

export const updateRole = (roleID, role) => dispatch =>
  new Promise((resolve, reject) => {
    updateRoleService(roleID, role)
      .then(response => {
        dispatch({
          type: "ROLE_DETAILS",
          payload: response.Role,
        });
        resolve("success");
      })
      .catch(error => {
        reject(error);
      });
  });

export const createRole = role => () =>
  new Promise((resolve, reject) => {
    createRoleService(role)
      .then(() => {
        resolve("success");
      })
      .catch(error => {
        reject(error);
      });
  });

export const loadOwnerData = user =>
  new Promise((resolve, reject) => {
    searchUser(user)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        console.error("error", error);
        reject(error);
      });
  });



// WEBPACK FOOTER //
// ./src/actions/UserActions/userActions.js